import { useCallback, useContext, useMemo, useRef, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import { Asset, AssetCollection, Index, getHorizon } from "wealthmode-frontend/backend"
import { Box, Button, ButtonIcon, ButtonText, HStack, Text, VStack } from "wealthmode-frontend/gluestack/ui"
import { PlusIcon } from "wealthmode-frontend/icons"
import { formatPercentage } from "wealthmode-frontend/util"

import { DataTable, IDLink } from "@/components"
import { BackendContext } from "@/context"

import { AddAssetModal } from "./AddAssetModal"

export default function Assets() {
  const backend = useContext(BackendContext)
  const { offset, limit } = useLocalSearchParams<{ offset?: string; limit?: string }>()

  const assetsCollectionRef = useRef<AssetCollection | null>(null)

  const [addOpen, setAddOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState<number | undefined>(undefined)

  const load = useCallback(async () => {
    var data = backend.link("data").resolve() as Index
    await data.fetch({ reload: true })

    var assets = data.link("assets")?.resolve() as AssetCollection | undefined
    if (!assets) {
      assetsCollectionRef.current = null
      return undefined
    }

    assetsCollectionRef.current = assets

    const query = {} as Record<string, string>
    if (offset) {
      query["offset"] = offset
    }
    if (limit) {
      query["limit"] = limit
    }

    await assets.fetch({
      query: new URLSearchParams(query),
      reload: true,
    })

    return assets
  }, [backend, offset, limit])

  const header = useMemo(
    () => [
      { id: "id", title: "ID", shrink: true },
      { id: "symbol", title: "Symbol", shrink: true },
      { id: "type", title: "Type", shrink: true },
      { id: "name", title: "Name" },
      { id: "expense_ratio", title: "Expense Ratio", shrink: true },
      { id: "cagr-1", title: "1Y CAGR", shrink: true },
      { id: "cagr-3", title: "3Y CAGR", shrink: true },
      { id: "cagr-5", title: "5Y CAGR", shrink: true },
      { id: "cagr-10", title: "10Y CAGR", shrink: true },
      { id: "cagr-20", title: "20Y CAGR", shrink: true },
      { id: "cagr-max", title: "Max CAGR", shrink: true },
      { id: "links", title: "Links", noTruncate: true, shrink: true },
    ],
    [],
  )

  const render = useCallback((model: Asset, field: string) => {
    function percentage(value: string | number | null | undefined) {
      return value ? formatPercentage(+value) : "-"
    }

    switch (field) {
      case "id":
        return (
          <IDLink
            href={`/assets/${model.attributes.id}`}
            id={model.attributes.id}
          />
        )
      case "cagr-1":
        return percentage(getHorizon(model.attributes.cagr ?? undefined, 1))
      case "cagr-3":
        return percentage(getHorizon(model.attributes.cagr ?? undefined, 3))
      case "cagr-5":
        return percentage(getHorizon(model.attributes.cagr ?? undefined, 5))
      case "cagr-10":
        return percentage(getHorizon(model.attributes.cagr ?? undefined, 10))
      case "cagr-20":
        return percentage(getHorizon(model.attributes.cagr ?? undefined, 20))
      case "cagr-max":
        return percentage(model.attributes.cagr?.max)
      case "expense_ratio":
        return percentage(model.attributes.expense_ratio)
      case "links":
        return (
          <Link href={`/assets/${model.attributes.id}/history`}>
            <Text className="text-markdown-link hover:opacity-80 underline">History</Text>
          </Link>
        )
    }
    return model.get(field)
  }, [])

  const onAddSuccess = useCallback(() => {
    setRefreshKey((key) => (key ?? 0) + 1)
  }, [])

  return (
    <Box>
      <VStack className="gap-4">
        <HStack className="justify-start">
          <Button
            size="sm"
            action="primary"
            onPress={() => setAddOpen(true)}
            className="bg-background-contrast pl-4 pr-8"
          >
            <ButtonIcon
              className="text-foreground-contrast"
              as={PlusIcon}
            />
            <ButtonText className="text-foreground-contrast">Add</ButtonText>
          </Button>
        </HStack>
        <DataTable
          title={"Assets"}
          load={load}
          refreshKey={refreshKey}
          header={header}
          render={render}
        />
      </VStack>

      <AddAssetModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={onAddSuccess}
        assetsCollectionRef={assetsCollectionRef}
      />
    </Box>
  )
}
